/**
 * Course Insights — response tracking + analytics for one LMS course.
 *
 * Per-module and overall: enrollment/completion, assessment scores, per-question
 * choice distributions, and intent/sentiment analysis of free-text answers
 * (classified server-side with Claude, the same way inbound communication
 * responses are analyzed). Data:
 *   GET  /api/lms/admin/courses/{id}/analytics
 *   GET  /api/lms/admin/courses/{id}/response-insights
 *   POST /api/lms/admin/courses/{id}/response-insights/analyze
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BarChart2, Loader2, MessageSquare, RefreshCw, Sparkles } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { authFetch } from '@/lib/authFetch';
import WorkforceLayout from '../layout';

const activeCboId = () => localStorage.getItem('active_cbo_id') || '';
const api = (p: string) => getWeaveinApiUrl(p);

interface LessonStat {
  lesson_id: number;
  title: string;
  content_type: string;
  completed_count: number;
  completion_pct: number;
  avg_score: number | null;
  pass_rate: number | null;
  questions: {
    question_index: number;
    prompt: string | null;
    is_text: boolean;
    text_response_count: number;
    options: { option: string; count: number }[];
  }[];
}
interface ModuleStat {
  module_id: number;
  title: string;
  completion_pct: number;
  lessons: LessonStat[];
}
interface Analytics {
  course_id: number;
  title: string;
  enrollments: { total: number; completed: number; in_progress: number };
  overall: { avg_module_completion_pct: number; avg_assessment_score: number | null };
  modules: ModuleStat[];
}
interface Insights {
  totals: { responses: number; classified: number };
  by_intent: Record<string, number>;
  by_sentiment: Record<string, number>;
  by_module: { module_id: number; title: string; responses: number; positive: number; negative: number; needs_attention: number }[];
  responses: {
    id: number;
    module_title: string;
    lesson_title: string;
    question_prompt: string | null;
    answer_text: string;
    intent_label: string | null;
    sentiment: string | null;
    summary: string | null;
  }[];
}

const INTENT_COLORS: Record<string, string> = {
  positive_feedback: 'bg-emerald-100 text-emerald-700',
  negative_feedback: 'bg-red-100 text-red-700',
  question: 'bg-blue-100 text-blue-700',
  request_for_help: 'bg-amber-100 text-amber-700',
  suggestion: 'bg-purple-100 text-purple-700',
  barrier: 'bg-orange-100 text-orange-700',
  other: 'bg-gray-100 text-gray-600',
};
const SENTIMENT_COLORS: Record<string, string> = {
  positive: 'bg-emerald-100 text-emerald-700',
  neutral: 'bg-gray-100 text-gray-600',
  negative: 'bg-red-100 text-red-700',
};

export default function CourseInsightsPage() {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const cbo = activeCboId();

  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [insights, setInsights] = useState<Insights | null>(null);
  const [moduleFilter, setModuleFilter] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!courseId) return;
    setLoading(true);
    setError('');
    try {
      const [aRes, iRes] = await Promise.all([
        authFetch(api(`/api/lms/admin/courses/${courseId}/analytics?cbo_id=${cbo}`)),
        authFetch(
          api(
            `/api/lms/admin/courses/${courseId}/response-insights?cbo_id=${cbo}${
              moduleFilter ? `&module_id=${moduleFilter}` : ''
            }`,
          ),
        ),
      ]);
      if (!aRes.ok) throw new Error(aRes.status === 403 ? 'Admin access required.' : 'Course not found');
      setAnalytics(await aRes.json());
      if (iRes.ok) setInsights(await iRes.json());
    } catch (err: any) {
      setError(err.message || 'Failed to load insights');
    } finally {
      setLoading(false);
    }
  }, [courseId, cbo, moduleFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const analyzeNow = async () => {
    if (!courseId) return;
    setAnalyzing(true);
    try {
      await authFetch(api(`/api/lms/admin/courses/${courseId}/response-insights/analyze?cbo_id=${cbo}`), {
        method: 'POST',
      });
      await load();
    } catch (err) {
      console.error('Analyze failed:', err);
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading && !analytics) {
    return (
      <WorkforceLayout>
        <div className="flex items-center justify-center py-24 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading insights…
        </div>
      </WorkforceLayout>
    );
  }
  if (!analytics) {
    return (
      <WorkforceLayout>
        <div className="text-center py-24 text-gray-500">
          <p className="mb-4">{error || 'Course not found.'}</p>
          <Button variant="outline" onClick={() => navigate(`/workforce/courses/${courseId}`)}>
            Back to course
          </Button>
        </div>
      </WorkforceLayout>
    );
  }

  return (
    <WorkforceLayout>
      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/workforce/courses/${courseId}`)} className="gap-1 shrink-0">
              <ArrowLeft className="w-4 h-4" /> Course
            </Button>
            <h1 className="font-semibold text-ink truncate flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-teal-600" /> {analytics.title} — Insights
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-1" onClick={load} disabled={loading}>
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button size="sm" className="gap-1" onClick={analyzeNow} disabled={analyzing}>
              {analyzing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              Analyze responses now
            </Button>
          </div>
        </div>

        {/* Overall stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Enrolled', value: analytics.enrollments.total },
            { label: 'Completed course', value: analytics.enrollments.completed },
            { label: 'Avg module completion', value: `${analytics.overall.avg_module_completion_pct}%` },
            {
              label: 'Avg assessment score',
              value: analytics.overall.avg_assessment_score != null ? `${analytics.overall.avg_assessment_score}%` : '—',
            },
            { label: 'Written responses', value: insights?.totals.responses ?? 0 },
          ].map((s) => (
            <div key={s.label} className="bg-surface rounded-xl p-4 border border-v2border">
              <p className="text-xs text-ink-500">{s.label}</p>
              <p className="font-display text-2xl font-semibold text-ink">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Per-module analytics */}
        <div className="bg-surface rounded-xl border border-v2border p-4">
          <h2 className="font-semibold text-ink mb-3">By module</h2>
          <div className="space-y-2">
            {analytics.modules.map((m) => {
              const ins = insights?.by_module.find((b) => b.module_id === m.module_id);
              const scores = m.lessons.filter((l) => l.avg_score != null);
              return (
                <button
                  key={m.module_id}
                  onClick={() => setModuleFilter(moduleFilter === m.module_id ? null : m.module_id)}
                  className={`w-full text-left rounded-lg border p-3 transition-colors ${
                    moduleFilter === m.module_id ? 'border-teal-400 bg-teal-50/50' : 'border-v2border hover:bg-paper'
                  }`}
                >
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <p className="text-sm font-medium text-ink truncate">{m.title}</p>
                    <div className="flex items-center gap-3 shrink-0 text-xs text-ink-500">
                      {scores.length > 0 && (
                        <span>
                          avg score{' '}
                          {Math.round(scores.reduce((a, l) => a + (l.avg_score || 0), 0) / scores.length)}%
                        </span>
                      )}
                      {ins && ins.responses > 0 && (
                        <span className="flex items-center gap-1">
                          <MessageSquare className="w-3 h-3" /> {ins.responses}
                        </span>
                      )}
                      {ins && ins.needs_attention > 0 && (
                        <span className="text-amber-600 font-medium">{ins.needs_attention} need attention</span>
                      )}
                      <span className="font-medium text-ink">{m.completion_pct}%</span>
                    </div>
                  </div>
                  <div className="h-2 bg-paper-2 rounded-full overflow-hidden">
                    <div className="h-full bg-teal-500 rounded-full" style={{ width: `${m.completion_pct}%` }} />
                  </div>
                </button>
              );
            })}
          </div>
          {moduleFilter && (
            <p className="text-xs text-ink-500 mt-2">
              Responses below are filtered to this module — click it again to see the whole course.
            </p>
          )}
        </div>

        {/* Response insights */}
        <div className="bg-surface rounded-xl border border-v2border p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-ink">
              Written responses {moduleFilter ? '(module)' : '(overall)'}
            </h2>
            <p className="text-xs text-ink-500">
              {insights?.totals.classified ?? 0}/{insights?.totals.responses ?? 0} analyzed
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {Object.entries(insights?.by_intent || {}).map(([k, n]) => (
              <span key={k} className={`px-2.5 py-1 rounded-full text-xs font-medium ${INTENT_COLORS[k] || INTENT_COLORS.other}`}>
                {k.replace(/_/g, ' ')}: {n}
              </span>
            ))}
            {Object.entries(insights?.by_sentiment || {}).map(([k, n]) => (
              <span key={`s-${k}`} className={`px-2.5 py-1 rounded-full text-xs font-medium ${SENTIMENT_COLORS[k] || SENTIMENT_COLORS.neutral}`}>
                {k}: {n}
              </span>
            ))}
          </div>

          {(insights?.responses || []).length === 0 ? (
            <p className="text-sm text-ink-500 py-6 text-center">
              No written responses yet. Free-text answers from quiz/survey components appear here and are
              classified automatically.
            </p>
          ) : (
            <div className="overflow-x-auto border border-v2border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-paper text-xs text-ink-500">
                  <tr>
                    <th className="text-left px-3 py-2">Module</th>
                    <th className="text-left px-3 py-2">Question</th>
                    <th className="text-left px-3 py-2">Response</th>
                    <th className="text-left px-3 py-2">Intent</th>
                    <th className="text-left px-3 py-2">Sentiment</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-v2border-muted align-top">
                  {insights!.responses.map((r) => (
                    <tr key={r.id}>
                      <td className="px-3 py-2 text-xs text-ink-500 whitespace-nowrap max-w-[160px] truncate">{r.module_title}</td>
                      <td className="px-3 py-2 text-xs text-ink-500 max-w-[200px]">{r.question_prompt || '—'}</td>
                      <td className="px-3 py-2 max-w-[360px]">
                        <p className="text-ink">{r.answer_text}</p>
                        {r.summary && <p className="text-xs text-ink-400 mt-0.5">{r.summary}</p>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {r.intent_label ? (
                          <span className={`px-2 py-0.5 rounded-full text-xs ${INTENT_COLORS[r.intent_label] || INTENT_COLORS.other}`}>
                            {r.intent_label.replace(/_/g, ' ')}
                          </span>
                        ) : (
                          <span className="text-xs text-ink-400">pending</span>
                        )}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {r.sentiment ? (
                          <span className={`px-2 py-0.5 rounded-full text-xs ${SENTIMENT_COLORS[r.sentiment] || SENTIMENT_COLORS.neutral}`}>
                            {r.sentiment}
                          </span>
                        ) : (
                          <span className="text-xs text-ink-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </WorkforceLayout>
  );
}
